import Avo from '@susu/custom-made/Avo';
import type { typeCard } from '@susu/custom-made/components/card/card';
import { CardOrientation } from '@susu/custom-made/components/card/card';
import { Clients } from '@susu/custom-made/utils/constants';
import type { Option, typeFabricList, typeOptionValue } from '@susu/custom-made/utils/interfaces';
import { getConfigurationTypeBySection } from '@susu/custom-made/utils/navigation';
import type { ConfigurationTypes } from '@susu/custom-made/utils/productDefinition';
import { CARD_LIST_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { getConfigurationTypeKey, onEnter } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useI18N } from '@susu/i18n/client';

import CardListBaseStyle from './cardListBaseStyle';
import CardListFabric from './cardListFabric';
import CardListInStore from './cardListInStore';
import CardListOption from './cardListOption';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import { useAvo } from '@susu/custom-made/hooks/useAvo';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';

const { ENABLE_AVO_TRACKING } = FEATURE_TOGGLES;

const CARD_LIST_CONTAINER_DEFAULT =
  'flex items-center overflow-auto -m-6 pt-0 px-16 pb-16 max-w-[calc(100vw+12px)] md:-m-[9px] md:px-20 md:pb-[23px] md:max-w-[calc(100vw+18px)] lg:block lg:w-full lg:m-0 lg:px-[9px] lg:pb-[9px] lg:overflow-visible 2xl:px-[11px] 2xl:pb-[11px] 2xl:max-w-full';

const CARD_LIST_CONTAINER_VERTICAL =
  'block items-center w-[calc(100%+12px)] overflow-auto -m-6 pt-0 px-16 pb-6 max-w-[calc(100vw+12px)] md:px-20 md:max-w-[calc(100vw+18px)] lg:m-0 lg:px-[9px] lg:pb-[9px] lg:overflow-visible lg:max-w-full 2xl:px-[11px] 2xl:pb-[11px]';

const CARD_LIST_CONTAINER_PRESENTATION =
  'block items-center overflow-auto m-0 p-0 w-full max-w-[calc(100vw+12px)] md:w-[340px] md:max-w-[calc(100vw+18px)] lg:w-[320px] lg:overflow-visible 2xl:w-[400px] 2xl:max-w-full';

const CARD_LIST_CONTAINER_DEFAULT_MOBILE =
  'flex items-center overflow-auto -m-6 pt-0 px-16 pb-16 max-w-[calc(100vw+12px)] md:-m-[9px] md:px-20 md:pb-[23px] md:max-w-[calc(100vw+18px)]';

const CARD_LIST_CONTAINER_VERTICAL_MOBILE =
  'block items-center w-[calc(100%+12px)] overflow-auto -m-6 pt-0 px-16 pb-6 max-w-[calc(100vw+12px)] md:px-20 md:max-w-[calc(100vw+18px)]';

/**
 * `lg:self-start` is load-bearing: the scroll container is a flex row, so without it the grid
 * stretches to the container height while its rows overflow, putting `lg:pb-20` above the overflow.
 */
const CARD_LIST_GRID_LG =
  'lg:grid lg:grid-cols-2 lg:items-start lg:self-start lg:gap-x-14 lg:gap-y-20 lg:w-full lg:m-0 lg:px-20 lg:pb-20 lg:overflow-visible lg:max-w-full 2xl:grid-cols-3';

const getCardListContainerClasses = (
  cardOrientation: CardOrientation | undefined,
  isPresentationOnly: boolean | undefined,
  isGrid?: boolean,
) => {
  if (isPresentationOnly) {
    return CARD_LIST_CONTAINER_PRESENTATION;
  }
  if (isGrid) {
    return cn(
      cardOrientation === CardOrientation.Vertical
        ? CARD_LIST_CONTAINER_VERTICAL_MOBILE
        : CARD_LIST_CONTAINER_DEFAULT_MOBILE,
      CARD_LIST_GRID_LG,
    );
  }
  if (cardOrientation === CardOrientation.Vertical) {
    return CARD_LIST_CONTAINER_VERTICAL;
  }
  return CARD_LIST_CONTAINER_DEFAULT;
};

const CARD_LIST_CONTROL = 'cursor-pointer pb-1';

const CARD_LIST_CONTROL_CONTAINER =
  'w-56 h-[88px] bg-gray-200 flex justify-center items-center shrink-0 m-3 rounded-md cursor-pointer md:w-[76px] md:h-[116px] md:my-5 md:mx-[9px] lg:hidden';

const CARD_LIST_CONTROL_ICON = 'flex [&>i]:text-gray-800 [&>i]:text-xl md:[&>i]:text-2xl';

const CARD_LIST_CONTROL_TEXT =
  'text-gray-800 text-center text-[11px] font-light leading-[14px] pt-8 md:text-sm md:leading-[unset] lg:hidden';

const CARD_LIST_CONTROL_TEXT_DESKTOP =
  'hidden lg:flex lg:items-center lg:w-auto lg:rounded-md lg:text-sm lg:relative lg:font-light lg:leading-sm lg:border lg:border-gray-400 lg:shrink-0 lg:cursor-pointer lg:pt-[11px] lg:px-0 lg:pb-[15px] lg:justify-center lg:mt-[11px] lg:mx-[11px] lg:mb-24 lg:before:h-20 lg:before:w-20 lg:before:text-xl lg:before:leading-none lg:before:font-[family-name:var(--font-icons)] lg:before:text-gray-800 lg:before:mr-4 2xl:mt-[13px] 2xl:mx-[13px] 2xl:mb-[13px]';

const CARD_LIST_PRESENTATION_TITLE =
  'overflow-hidden text-gray-800 text-ellipsis text-sm font-medium leading-xs pt-[26px] px-0 pb-10 md:text-base md:leading-base md:pt-32 lg:text-sm lg:leading-xs lg:pb-8';

export enum CardListType {
  Fabric,
  Option,
}

export type cardItemsType = number[] | typeFabricList | Option[] | typeOptionValue;

export default function CardList({
  cardItems,
  cardSubItems,
  selectedItemId,
  cardOrientation,
  isGroup,
  isBaseStyleActive,
  cardListType,
  optionValues,
  showAdvancedButton,
  showRemoveAdditionalProductButton,
  testId,
  isPresentationOnly,
  isDetailedView,
  cardClick = () => {},
  infoIconClick,
  baseStyleClick,
  additionalProductRemoveClick,
  scanClick,
  showAdvancedItems = false,
  section,
  selectedOption,
  cardItemRef,
  affectedConfigurationTypeId,
  isGrid = false,
}: Readonly<{
  cardItems: cardItemsType;
  cardSubItems?: (string | number)[];
  selectedItemId?: number | null;
  cardOrientation?: CardOrientation;
  isGroup?: boolean;
  isBaseStyleActive?: boolean;
  cardListType: CardListType;
  optionValues?: typeOptionValue;
  showAdvancedButton?: boolean;
  showRemoveAdditionalProductButton?: boolean;
  isPresentationOnly?: boolean;
  isDetailedView?: boolean;
  cardClick?: (card: typeCard) => void;
  infoIconClick?: (card: typeCard) => void;
  baseStyleClick?: () => void;
  additionalProductRemoveClick?: () => void;
  scanClick?: () => void;
  testId?: string;
  showAdvancedItems?: boolean;
  section: string | null;
  selectedOption?: Option | null;
  cardItemRef?: RefObject<HTMLDivElement>;
  affectedConfigurationTypeId?: ConfigurationTypes;
  isGrid?: boolean;
}>) {
  const { t } = useI18N();
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState<boolean>(showAdvancedItems);
  const { client } = useUserContext();
  const { selectedBaseStyle } = useOptionContext();
  const { getAvoStandardEventParams } = useAvo();
  const isAvoTrackingEnabled = useFeatureIsOn(ENABLE_AVO_TRACKING);
  const cardlistRef = useRef<HTMLInputElement>(null);

  const stylePresetSelectedValue = () => {
    const sectionConfigurationType = getConfigurationTypeBySection(section ?? '');

    return selectedBaseStyle[sectionConfigurationType]
      ? selectedBaseStyle[sectionConfigurationType].name
      : t('cardList.noSelectedPreset');
  };

  const advanceClick = () => {
    setIsAdvancedExpanded(true);
    if (isAvoTrackingEnabled) {
      Avo.showAdvancedOptions({
        ...getAvoStandardEventParams(`${section}_options`),
      });
    }
  };

  useEffect(() => {
    cardlistRef.current?.scrollTo(0, 0);
  }, [cardItems, cardSubItems]);

  return (
    <div
      className={getCardListContainerClasses(cardOrientation, isPresentationOnly, isGrid)}
      ref={cardlistRef}
      data-testid={testId}
    >
      {showRemoveAdditionalProductButton && (
        <div className={CARD_LIST_CONTROL}>
          <div
            data-testid={CARD_LIST_TEST_IDS.getRemoveBtnMobile(section ?? '')}
            onClick={additionalProductRemoveClick}
            onKeyDown={e => {
              onEnter(e, () => {
                if (additionalProductRemoveClick) {
                  additionalProductRemoveClick();
                }
              });
            }}
            className={CARD_LIST_CONTROL_CONTAINER}
          >
            <div className={CARD_LIST_CONTROL_ICON}>
              <i className="susu-icons" translate="no">
                none
              </i>
            </div>
          </div>
          <div className={CARD_LIST_CONTROL_TEXT}>{t('cardList.remove')}</div>
          <div
            className={cn(CARD_LIST_CONTROL_TEXT_DESKTOP, "lg:before:content-['none']")}
            data-testid={CARD_LIST_TEST_IDS.getRemoveBtnDesktop(section ?? '')}
            onClick={additionalProductRemoveClick}
            onKeyDown={e => {
              onEnter(e, () => {
                if (additionalProductRemoveClick) {
                  additionalProductRemoveClick();
                }
              });
            }}
          >
            {t('cardList.removeItem', {
              item: t(`configurationType.${getConfigurationTypeKey(getConfigurationTypeBySection(section!))}`),
            })}
          </div>
        </div>
      )}
      {isBaseStyleActive && stylePresetSelectedValue() && (
        <CardListBaseStyle
          isPresentationOnly={isPresentationOnly}
          baseStyleClick={baseStyleClick}
          stylePresetSelectedValue={stylePresetSelectedValue}
        />
      )}
      {isBaseStyleActive && isPresentationOnly && (
        <div className={CARD_LIST_PRESENTATION_TITLE}>{t('cardList.designOptions')}</div>
      )}
      {client === Clients.inStore && cardListType === CardListType.Fabric && scanClick && (
        <CardListInStore scanClick={scanClick} />
      )}
      {cardListType === CardListType.Fabric && (
        <CardListFabric
          cardItems={cardItems}
          cardSubItems={cardSubItems}
          selectedItemId={selectedItemId}
          cardOrientation={cardOrientation}
          cardClick={cardClick}
          infoIconClick={infoIconClick}
          isGrid={isGrid}
        />
      )}
      {cardListType === CardListType.Option && (
        <CardListOption
          cardItems={cardItems}
          cardSubItems={cardSubItems}
          selectedItemId={selectedItemId}
          cardOrientation={cardOrientation}
          isGroup={isGroup}
          isAdvancedExpanded={isAdvancedExpanded}
          optionValues={optionValues}
          isPresentationOnly={isPresentationOnly}
          cardClick={cardClick}
          selectedOption={selectedOption}
          isDetailedView={isDetailedView}
          cardItemRef={cardItemRef}
          affectedConfigurationTypeId={affectedConfigurationTypeId}
          isGrid={isGrid}
        />
      )}
      {showAdvancedButton && !isAdvancedExpanded && (
        <div className={CARD_LIST_CONTROL}>
          <div
            onClick={advanceClick}
            data-testid={CARD_LIST_TEST_IDS.ADVANCED_OPTIONS_BTN_MOBILE}
            onKeyDown={e => {
              onEnter(e, advanceClick);
            }}
            className={CARD_LIST_CONTROL_CONTAINER}
          >
            <div className={CARD_LIST_CONTROL_ICON}>
              <i className="susu-icons" translate="no">
                arrow_right
              </i>
            </div>
          </div>
          <div className={CARD_LIST_CONTROL_TEXT}>{t('cardList.advanced')}</div>
          <div
            className={cn(CARD_LIST_CONTROL_TEXT_DESKTOP, "lg:before:content-['arrow_down']")}
            data-testid={CARD_LIST_TEST_IDS.ADVANCED_OPTIONS_BTN_DESKTOP}
            onClick={advanceClick}
            onKeyDown={e => {
              onEnter(e, advanceClick);
            }}
          >
            {t('cardList.advancedOptions')}
          </div>
        </div>
      )}
    </div>
  );
}
